import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ICrmError, IMessageTemplate, ITemplatePreview } from "./types";

const adminUrl = `${environment.v2Url}/crm-admin`;

export interface ITemplateDraft { subject?: string | null; body?: string | null }
export interface IPreviewOptions { phone?: boolean; confirmed?: boolean }

export type CrmTemplatesKeyFunctions = {
    list: () => Promise<IMessageTemplate[] | ICrmError>;
    preview: (key: string, draft: ITemplateDraft, opts?: IPreviewOptions) => Promise<ITemplatePreview | ICrmError>;
    save: (key: string, patch: ITemplateDraft & { enabled?: boolean }) => Promise<IMessageTemplate | ICrmError>;
    reset: (key: string) => Promise<IMessageTemplate | ICrmError>;
    /** Sends the draft (with sample data) to the logged-in user only. */
    sendTest: (key: string, draft: ITemplateDraft, opts?: IPreviewOptions) => Promise<{ sent_to: string } | ICrmError>;
};

// #region useCrmTemplates
/** Manager-only: every email the system sends, and managers' edits to them. */
export const useCrmTemplates = (): [CrmTemplatesKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };
    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return { ok: false, status, message: err?.response?.data?.message || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.') };
    };
    const call = async <T>(method: 'get' | 'post' | 'put' | 'delete', path: string, body?: unknown): Promise<T | ICrmError> => {
        try {
            const r: AxiosResponse<T> = await axios.request({ method, url: `${adminUrl}${path}`, data: body, headers });
            return r.data;
        } catch (err: any) { return handle(err); }
    };
    const k = (key: string) => `/templates/${encodeURIComponent(key)}`;

    return [{
        list: () => call<IMessageTemplate[]>('get', '/templates'),
        preview: (key, draft, opts) => call<ITemplatePreview>('post', `${k(key)}/preview`, { ...draft, ...opts }),
        save: (key, patch) => call<IMessageTemplate>('put', k(key), patch),
        reset: (key) => call<IMessageTemplate>('delete', k(key)),
        sendTest: (key, draft, opts) => call<{ sent_to: string }>('post', `${k(key)}/test`, { ...draft, ...opts })
    }];
};
